// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import exportCustomerReferenceNumbers from "@mcmc/App/Handlers/CustomerReference/ExportCustomerReferenceNumbers";
import loadCustomerReferenceNumbers from "@mcmc/App/Handlers/CustomerReference/LoadCustomerReferenceNumbers";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import {
    getCustomerReferenceNumbers,
    getCustomerReferenceNumbersLoadingState,
} from "@mcmc/App/Store/Selectors/CustomerReferenceNumbersSelector";
import { styledBox } from "@mcmc/XDL/Components/Common/Box";
import Notify from "@mcmc/XDL/Components/Notify";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: McMcApplicationState) => ({
    currentBillTo: getSession(state).billToId,
    customerReferenceNumbers: getCustomerReferenceNumbers(state),
    isLoading: getCustomerReferenceNumbersLoadingState(state),
});

const mapDispatchToProps = {
    loadCustomerReferenceNumbers,
    exportCustomerReferenceNumbers,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CustomerReferenceNumbersStyles {
    mainWrapper?: InjectableCss;
    dataTableWrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    exportWrapper?: InjectableCss;
    exportButton?: ButtonPresentationProps;
    exportInstructions?: TypographyPresentationProps;
}

export const customersReferencesStyles: CustomerReferenceNumbersStyles = {
    mainWrapper: {
        css: css`
            ${styledBox};
            margin-top: 10px;
            margin-bottom: 0px;
        `,
    },
    dataTableWrapper: {
        css: css`
            overflow-x: auto;
            position: relative;
            padding-top: 3px;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    exportWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            margin-top: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-wrap: wrap;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    exportButton: {
        css: css`
            margin-left: 10px;
            align-self: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 10px;
                            margin-left: 0;
                            width: 100%;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
};

const styles = customersReferencesStyles;

const CustomerReferenceNumbers: React.FC<Props> = ({
    loadCustomerReferenceNumbers,
    currentBillTo,
    customerReferenceNumbers,
    exportCustomerReferenceNumbers,
    isLoading,
}) => {
    React.useEffect(() => {
        if (currentBillTo) {
            loadCustomerReferenceNumbers({ billToId: currentBillTo });
        }
    }, [currentBillTo]);

    const handleExportReferencesToExcel = () => {
        if (currentBillTo) {
            exportCustomerReferenceNumbers({ billToId: currentBillTo });
        }
    };

    const { productReferenceNumberCollection = [] } = customerReferenceNumbers || {};

    if (isLoading || !customerReferenceNumbers) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }
    if (productReferenceNumberCollection.length === 0) {
        return <Notify messageType="danger" hideCloseButton body={translate("No Data Found.")} />;
    }

    return (
        <StyledWrapper {...styles.mainWrapper}>
            <StyledWrapper {...styles.dataTableWrapper}>
                <DataTable>
                    <DataTableHead>
                        <DataTableHeader className="no-sticky">{translate("Part Number")}</DataTableHeader>
                        <DataTableHeader>{translate("Ref #")}</DataTableHeader>
                        <DataTableHeader>{translate("Customer Number")}</DataTableHeader>
                        <DataTableHeader>{translate("Ship-To Customer Name")}</DataTableHeader>
                        <DataTableHeader>{translate("Date Last Updated")}</DataTableHeader>
                    </DataTableHead>
                    <DataTableBody>
                        {productReferenceNumberCollection.map(ref => (
                            <DataTableRow key={`${ref.customerNumber}-${ref.shipToCustomerName}-${ref.refNumber}`}>
                                <DataTableCell className="no-sticky">{ref.productMfgNumber}</DataTableCell>
                                <DataTableCell>{ref.refNumber}</DataTableCell>
                                <DataTableCell>{ref.customerNumber}</DataTableCell>
                                <DataTableCell>{ref.shipToCustomerName}</DataTableCell>
                                <DataTableCell>{ref.lastUpdatedOn}</DataTableCell>
                            </DataTableRow>
                        ))}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
            <StyledWrapper {...styles.exportWrapper}>
                <Typography {...styles.exportInstructions}>
                    {siteMessage("McMc_Account_Customer_Reference_Numbers_Export_Instructional")}
                </Typography>
                <Button {...styles.exportButton} onClick={handleExportReferencesToExcel}>
                    {translate("Export")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const customerReferenceNumbersWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CustomerReferenceNumbers),
    definition: {
        group: "My Account",
        allowedContexts: ["CustomerReferenceNumbersPage" as any],
    },
};

export default customerReferenceNumbersWidget;
